import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  LigneAlerte,
  MessageErreur,
  Pastille,
  PastilleSite,
  MAX_ALERTES_TABLEAU_BORD,
  PiedAlertes,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { useSites } from '../lib/useSites';
import { IntegrationAlerts } from '../components/alertes/IntegrationAlerts';
import { useAlertes } from '../lib/useAlertes';
import { useDashboardPedagogique, type DonneesPedagogique } from '../lib/useDashboardPedagogique';

interface AlerteItem {
  titre: string;
  detail: string;
  ton?: 'danger' | 'warning' | 'info';
  site?: string;
}

const ACCES_RAPIDES = [
  {
    libelle: "Vue d'ensemble",
    description: 'Toutes mes rubriques en une page',
    ecran: 'ped-hub',
  },
  {
    libelle: 'Intégration & convocations',
    description: 'Alerte nouveau contrat, entrée imminente',
    ecran: 'ped-integration',
  },
  {
    libelle: 'Absences & avertissements',
    description: 'Mail entreprise, seuils',
    ecran: 'ped-absences',
  },
  {
    libelle: 'Apprenants sans entreprise',
    description: "Vue filtrable, diffusion d'offres",
    ecran: 'ped-sanscontrat',
  },
];

/**
 * Tableau de bord pédagogique — mise en page de la maquette validée
 * (docs/maquette/modules/10-apprenant-facturation-direction.js, `dashPedagogique()`
 * et `poleDash()`).
 *
 * Compteurs et tâches viennent de `GET /api/dashboard/pedagogique` : chaque
 * tâche désigne un dossier réel, et l'API ne rend que celles dont l'écran est
 * ouvert au profil — l'assistant(e) de site ne reçoit pas la correction des
 * tests (CDC §6.1). Rien n'est complété à l'écran : une liste vide se dit.
 */
export function DashboardPedagogiquePage(): JSX.Element {
  const { utilisateur, peutAcceder } = useAuth();
  const navigate = useNavigate();
  const { site } = usePerimetre();
  const { data: sitesData } = useSites();
  const { data, isLoading, error } = useDashboardPedagogique(site);

  const isAssistant = utilisateur?.sousProfil === 'ASSISTANT';

  const perimetre = useMemo(
    () => (sitesData?.sites ? sitesData.sites.map((s) => s.code) : []),
    [sitesData],
  );
  const tous = !site || site === TOUS_LES_SITES;

  const { data: alertesData } = useAlertes(tous ? '' : site);

  const alertesBrutes = alertesData?.dernieres?.map(a => ({
    titre: a.libelleRegle,
    detail: a.message,
    ton: (a.regle === 'VEILLE_ECHEANCE' || a.regle === 'FIN_FORMATION_APPROCHANT') ? 'warning' : 'info',
    site: a.siteCode ?? site,
  })) as AlerteItem[] || [];

  const alertes: AlerteItem[] = alertesBrutes.length
    ? alertesBrutes
    : [
        {
          titre: 'Aucune alerte active',
          detail: `Rien à signaler sur ${site}`,
          ton: 'info',
          site: site ?? '',
        },
      ];

  const sousTitre = tous
    ? `Vue consolidée · ${perimetre.length} site(s) attribué(s)`
    : `Site de ${site} · données limitées à ce périmètre`;

  const listTitle = `À traiter aujourd'hui${tous ? ' · tous sites' : ` · ${site}`}`;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Pôle pédagogique"
        sousTitre={sousTitre}
        actions={
          <BoutonPrincipal onClick={() => navigate('/ped-integration')}>
            Envoyer les convocations
          </BoutonPrincipal>
        }
      />

      <BarreSites />

      {isLoading && <Chargement libelle="Chargement du tableau de bord…" />}
      {!isLoading && (error || !data) && (
        <MessageErreur message="Impossible de charger le tableau de bord." />
      )}

      {data && (
        <>
          <CompteursPedagogiques
            compteurs={data.compteurs}
            detailApprenants={
              tous
                ? perimetre.join(' · ')
                : `${data.compteurs.formations} formations · ${data.compteurs.formateurs} formateurs`
            }
            isAssistant={isAssistant}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2 min-w-0">
              <Carte titre={listTitle}>
                {data.taches.length === 0 ? (
                  <div className="flex items-center gap-4 px-4 sm:px-6 py-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-codialis-navy">
                        Rien à traiter sur ce périmètre
                      </div>
                      <div className="text-[11px] text-codialis-gray">
                        Aucune action en attente
                      </div>
                    </div>
                    <Pastille texte="À jour" ton="success" />
                  </div>
                ) : (
                  data.taches.map((tache) => (
                    <button
                      key={tache.id}
                      type="button"
                      onClick={() => navigate(tache.page)}
                      className="flex w-full items-center gap-4 px-4 sm:px-6 py-4 text-left transition hover:bg-gray-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-codialis-navy">{tache.titre}</div>
                        <div className="text-[11px] text-codialis-gray">{tache.sousTitre}</div>
                      </div>
                      {tache.site && <PastilleSite site={tache.site} />}
                      <Pastille texte={tache.statut} ton={tache.ton} />
                    </button>
                  ))
                )}
              </Carte>

              <Carte titre="Accès rapides" sansBordure>
                <div className="grid grid-cols-1 gap-3 p-4 sm:p-6 sm:grid-cols-2">
                  {ACCES_RAPIDES.filter((item) => peutAcceder(item.ecran)).map((item) => (
                    <button
                      key={item.ecran}
                      type="button"
                      onClick={() => navigate(`/${item.ecran}`)}
                      className="rounded-xl bg-gray-50 p-4 text-left transition hover:bg-gray-100"
                    >
                      <div className="text-sm font-semibold text-codialis-navy">{item.libelle}</div>
                      <div className="mt-0.5 text-[11px] text-codialis-gray">{item.description}</div>
                    </button>
                  ))}
                </div>
              </Carte>
            </div>

            <div className="space-y-6">
              <IntegrationAlerts siteId={site} />
              <Carte
                titre="Mes alertes"
                actions={
                  <BoutonSecondaire onClick={() => navigate('/alertes')}>
                    Module d'alertes
                  </BoutonSecondaire>
                }
              >
                {alertes.slice(0, MAX_ALERTES_TABLEAU_BORD).map((a, index) => (
                  <LigneAlerte
                    key={index}
                    titre={a.titre}
                    detail={a.detail}
                    ton={a.ton}
                    site={a.site}
                  />
                ))}
                <PiedAlertes
                  restantes={alertes.length - MAX_ALERTES_TABLEAU_BORD}
                  onClick={() => navigate('/alertes')}
                />
              </Carte>

              {/* L'attestation se délivre dans « Documents pédagogiques », là
                  où elle est générée et rattachée au dossier. */}
              {isAssistant && (
                <Carte titre="Profil assistant(e) de site" sansBordure>
                  <div className="space-y-3 p-4 sm:p-6 text-sm text-codialis-gray">
                    <p>
                      Votre périmètre couvre le suivi des apprenants et la délivrance des
                      attestations d'entrée.{' '}
                      {data.compteurs.attestationsADelivrer > 0
                        ? `${data.compteurs.attestationsADelivrer} attestation(s) à délivrer.`
                        : 'Aucune attestation en attente.'}
                    </p>
                    <BoutonMint onClick={() => navigate('/ped-documents')}>
                      Délivrer une attestation d'entrée
                    </BoutonMint>
                  </div>
                </Carte>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CompteursPedagogiques({
  compteurs,
  detailApprenants,
  isAssistant,
}: {
  compteurs: DonneesPedagogique['compteurs'];
  detailApprenants: string;
  isAssistant: boolean;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Compteur
        libelle="Apprenants suivis"
        valeur={compteurs.apprenants}
        detail={detailApprenants}
        ton="navy"
      />
      <Compteur
        libelle={`Intégrations sous ${compteurs.preavisIntegrationJours} j`}
        valeur={compteurs.integrationsImminentes}
        detail="convocations à envoyer"
        ton="warm"
      />
      <Compteur
        libelle="Absences du jour"
        valeur={compteurs.absencesDuJour}
        detail={`${compteurs.seuilsDepasses} seuil(s) dépassé(s)`}
        ton="red"
      />
      {isAssistant ? (
        <Compteur
          libelle="Attestations à délivrer"
          valeur={compteurs.attestationsADelivrer}
          detail="entrée en formation"
          ton="mint"
        />
      ) : (
        <Compteur
          libelle="Fiches séquence à valider"
          valeur={compteurs.fichesAValider}
          detail="délai 8 jours"
          ton="blue"
        />
      )}
    </div>
  );
}
